import { useId, useMemo } from 'react';
import type { CSSProperties } from 'react';

export interface LuceNetworkGaugeProps {
  online: number;
  total: number;
  faulted?: number;
  size?: number;
}

// ── Gauge geometry ───────────────────────────────────────────────────
//
// Half-circle sweep from 9 o'clock to 3 o'clock. SVG angles measured
// from the positive-x axis (3 o'clock = 0°), so the needle rotates
// through -180° → 0° as the fraction climbs.
const START = -180;
const END = 0;
const SWEEP = END - START; // 180
const CENTRE = 120;
const R_ARC = 95;

// Tri-zone split: everything below 50% online reads as critical
// (warn / red), 50-80% as warning (power / amber), 80%+ as healthy
// (good / green). Matches the ChargingDial warning vocabulary.
const ZONE_CRIT_END = 50;
const ZONE_WARN_END = 80;

function polarToXY(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const start = polarToXY(cx, cy, r, startDeg);
  const end = polarToXY(cx, cy, r, endDeg);
  const large = endDeg - startDeg <= 180 ? 0 : 1;
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

function pctToAngle(pct: number): number {
  return START + (SWEEP * Math.max(0, Math.min(100, pct))) / 100;
}

/**
 * Luce-theme network status gauge — a half-circle instrument-cluster
 * sector dial with a sweeping needle, tri-zone warning arc, and an
 * illuminated tick ring. Replaces the ring/percentage shape the
 * Default variant uses.
 *
 * Canonical animation palette:
 *   - `rotate` on the needle group (sweeps from 0% on mount)
 *   - `opacity` on the faulted chip (pulsing when faulted > 0)
 * `filter: blur` is applied once on the drop-shadow filter and never
 * animated.
 *
 * `prefers-reduced-motion: reduce` clamps both animations to static.
 */
export function LuceNetworkGauge({
  online, total, faulted = 0, size = 240,
}: LuceNetworkGaugeProps) {
  const id = useId().replace(/:/g, '');
  const pct = total === 0 ? 0 : (online / total) * 100;
  const needleAngle = pctToAngle(pct);

  // 11 ticks at every 10% (0/10/20/…/100). Every other tick is major.
  const ticks = useMemo(() => {
    return Array.from({ length: 11 }).map((_, i) => {
      const tickPct = i * 10;
      const angle = pctToAngle(tickPct);
      const major = i % 2 === 0;
      const passed = tickPct <= pct;
      const zoneColor =
        tickPct < ZONE_CRIT_END ? 'var(--evi-warn)'
        : tickPct < ZONE_WARN_END ? 'var(--evi-power)'
        : 'var(--evi-good)';
      return { angle, major, passed, zoneColor };
    });
  }, [pct]);

  const vars: CSSProperties = {
    // Exposed so the keyframes can reference the initial / target
    // rotation via animation keyframes below.
    ['--evi-net-needle-start' as string]: `${START}deg`,
    ['--evi-net-needle-end' as string]: `${needleAngle}deg`,
  };

  return (
    <div
      style={{
        background: 'var(--evi-surface)',
        border: '1.5px solid var(--evi-border-strong)',
        borderRadius: 12,
        padding: 18,
        boxShadow: 'var(--evi-card-glow)',
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
        position: 'relative',
        overflow: 'hidden',
        ...vars,
      }}
      role="img"
      aria-label={`Network: ${online} of ${total} chargers online`}
    >
      <div
        style={{
          fontSize: 10,
          letterSpacing: 1.4,
          color: 'var(--evi-text-mute)',
          fontWeight: 600,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        Network status
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: size,
          aspectRatio: '1 / 0.66',
          margin: '0 auto',
        }}
      >
        <svg viewBox="0 0 240 158" width="100%" height="100%">
          <defs>
            <filter id={`luce-net-shadow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="1.2" stdDeviation="0.9" floodOpacity="0.5" />
            </filter>
          </defs>

          {/* Tri-zone backdrop arc — thin coloured underlay */}
          <path
            d={arcPath(CENTRE, CENTRE, R_ARC, START, pctToAngle(ZONE_CRIT_END))}
            fill="none"
            stroke="var(--evi-warn)"
            strokeWidth="3"
            opacity="0.4"
          />
          <path
            d={arcPath(CENTRE, CENTRE, R_ARC, pctToAngle(ZONE_CRIT_END), pctToAngle(ZONE_WARN_END))}
            fill="none"
            stroke="var(--evi-power)"
            strokeWidth="3"
            opacity="0.4"
          />
          <path
            d={arcPath(CENTRE, CENTRE, R_ARC, pctToAngle(ZONE_WARN_END), END)}
            fill="none"
            stroke="var(--evi-good)"
            strokeWidth="3"
            opacity="0.4"
          />

          {/* Tick ring — illuminated up to the needle */}
          {ticks.map((t, i) => {
            const inner = polarToXY(CENTRE, CENTRE, t.major ? 78 : 84, t.angle);
            const outer = polarToXY(CENTRE, CENTRE, R_ARC, t.angle);
            return (
              <line
                key={i}
                x1={inner.x.toFixed(2)}
                y1={inner.y.toFixed(2)}
                x2={outer.x.toFixed(2)}
                y2={outer.y.toFixed(2)}
                stroke={t.passed ? t.zoneColor : 'var(--evi-border-strong)'}
                strokeWidth={t.major ? 2.5 : 1.2}
                opacity={t.passed ? 1 : 0.55}
              />
            );
          })}

          {/* Needle — tapered polygon with a drop-shadow filter for
              depth. `transform: rotate` is animated on the group,
              origin at the gauge centre. */}
          <g
            className={`luce-net-needle-${id}`}
            style={{
              transformOrigin: `${CENTRE}px ${CENTRE}px`,
              transform: `rotate(${needleAngle - START}deg)`,
              animation: `evi-luce-net-needle-${id} 1.6s cubic-bezier(0.22, 1, 0.36, 1) forwards`,
            }}
            filter={`url(#luce-net-shadow-${id})`}
          >
            {/*
             * Base needle points LEFT (toward 9 o'clock / -180°), which
             * is where the gauge's 0% sits. The group rotation then
             * sweeps it clockwise to the current reading.
             */}
            <polygon
              points={`${CENTRE},${CENTRE - 3} ${CENTRE},${CENTRE + 3} ${CENTRE - 76},${CENTRE + 0.8} ${CENTRE - 76},${CENTRE - 0.8}`}
              fill="var(--evi-charging)"
            />
            <circle cx={CENTRE - 78} cy={CENTRE} r="2.2" fill="var(--evi-charging)" />
          </g>

          {/* Hub cap */}
          <circle
            cx={CENTRE} cy={CENTRE} r="9"
            fill="var(--evi-surface-2)"
            stroke="var(--evi-charging)"
            strokeWidth="1.5"
          />
          <circle cx={CENTRE} cy={CENTRE} r="3" fill="var(--evi-charging)" />
        </svg>

        {/* Digital readout sits inside the half-dial (SVG viewBox is
            240×158, so the center of the readout lines up under the
            needle's pivot without stacking below it). */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 4,
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              fontSize: 26,
              fontWeight: 300,
              color: 'var(--evi-text)',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: -0.5,
              lineHeight: 1,
            }}
          >
            {online}
            <span
              style={{
                color: 'var(--evi-text-mute)',
                fontSize: 16,
                fontWeight: 400,
                margin: '0 4px',
              }}
            >
              /
            </span>
            {total}
          </div>
          <div
            style={{
              fontSize: 9,
              letterSpacing: 1.6,
              color: 'var(--evi-text-mute)',
              fontWeight: 600,
              marginTop: 4,
              textTransform: 'uppercase',
            }}
          >
            Online
          </div>
        </div>
      </div>

      {/* Faulted indicator — only renders when the network has at
          least one faulted charger. Pulses subtly to draw the eye. */}
      {faulted > 0 && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 12,
            padding: '4px 10px',
            border: '1px solid var(--evi-warn)',
            borderRadius: 999,
            background: 'color-mix(in srgb, var(--evi-warn) 10%, transparent)',
            color: 'var(--evi-warn)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            animation: `evi-luce-net-faulted-${id} 2.4s ease-in-out infinite`,
          }}
        >
          <span aria-hidden>●</span>
          <span>{faulted} faulted</span>
        </div>
      )}

      <style>{`
        @keyframes evi-luce-net-needle-${id} {
          from { transform: rotate(0deg); }
          to   { transform: rotate(${needleAngle - START}deg); }
        }
        @keyframes evi-luce-net-faulted-${id} {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.55; }
        }
        @media (prefers-reduced-motion: reduce) {
          .luce-net-needle-${id} {
            animation: none !important;
            transform: rotate(${needleAngle - START}deg) !important;
          }
        }
      `}</style>
    </div>
  );
}
